import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../../components/TanTable';
import whseService, {
  type WhseReceivingRow,
  type WhseShippingRow,
  type WhseReceivingSummary,
  type WhseShippingSummary,
} from '../../../services/whse.service';

interface Props {
  /** Numeric order pronumber, or a "T"-prefixed template pronumber ("T110"). */
  pronumber: number | string;
}

/** Label:value cell used in the top summary block. */
const SummaryField: React.FC<{ label: string; value: string; strong?: boolean }> = ({
  label,
  value,
  strong,
}) => (
  <div className="flex text-xs leading-5">
    <span className="w-32 shrink-0 text-slate-500 dark:text-gray-400">{label}</span>
    <span
      className={`flex-1 min-w-0 break-words ${
        strong ? 'font-semibold' : ''
      } text-slate-800 dark:text-gray-100`}
    >
      {value || '—'}
    </span>
  </div>
);

const SummaryHeader: React.FC<{
  title: string;
  totalQty: string;
  whseInstructionsCount: number;
  fields: Array<{ label: string; value: string }>;
}> = ({ title, totalQty, whseInstructionsCount, fields }) => (
  <div className="rounded-lg border border-slate-200 dark:border-gray-700 bg-panel p-3">
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2.5">
      <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
        {title}
      </span>
      <span className="text-xs text-slate-700 dark:text-gray-200">
        <span className="text-slate-500 dark:text-gray-400">Total Qty:&nbsp;</span>
        <span className="font-semibold">{totalQty || '0'}</span>
      </span>
      <span className="text-xs text-slate-700 dark:text-gray-200">
        <span className="text-slate-500 dark:text-gray-400">Whse Instructions:&nbsp;</span>
        <span className="font-semibold">
          {whseInstructionsCount} Item{whseInstructionsCount === 1 ? '' : 's'}
        </span>
      </span>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-1">
      {fields.map((f) => (
        <SummaryField key={f.label} label={f.label} value={f.value} />
      ))}
    </div>
  </div>
);

/** Red-when-open text cell (holds / damages). */
const flagCell = (text: string, open: boolean) => (
  <span className={open ? 'text-rose-600 dark:text-rose-400 font-semibold' : ''}>{text}</span>
);

const RECEIVING_COLUMNS: ColumnDef<WhseReceivingRow>[] = [
  { accessorKey: 'rcvNum', header: 'MR #', size: 60 },
  { accessorKey: 'rcvWhse', header: 'Warehouse', size: 90 },
  { accessorKey: 'rcvPo', header: 'MR PO', size: 80 },
  { accessorKey: 'productDescription', header: 'Product Code', size: 200 },
  { accessorKey: 'packingListQty', header: 'P/L Qty', size: 70 },
  { accessorKey: 'packingListUom', header: 'P/L UOM', size: 70 },
  { accessorKey: 'rcvMasterQty', header: '1st Qty', size: 70 },
  { accessorKey: 'rcvMasterUom', header: '1st UoM', size: 70 },
  { accessorKey: 'rcvSecondaryQty', header: '2nd Qty', size: 70 },
  { accessorKey: 'rcvSecondaryUom', header: '2nd UoM', size: 70 },
  { accessorKey: 'rcvLbs', header: 'Lbs', size: 80 },
  { accessorKey: 'rcvKgs', header: 'Kgs', size: 80 },
  { accessorKey: 'consigneeName', header: 'Consignee', size: 140 },
  { accessorKey: 'consigneeInfo', header: 'Address/Contact', size: 150 },
  { accessorKey: 'rcvArriveDate', header: 'Arrive Date', size: 90 },
  { accessorKey: 'checkInDatetime', header: 'Check In', size: 120 },
  { accessorKey: 'rcvSplitFrom', header: 'Split From', size: 80 },
  { accessorKey: 'description', header: 'Description', size: 160 },
  { accessorKey: 'blPronumber', header: 'BL', size: 80 },
  { accessorKey: 'origMr', header: 'Orig MR', size: 90 },
  { accessorKey: 'transferMr', header: 'Transfer MR', size: 90 },
  {
    accessorKey: 'rcvHold',
    header: 'MR Hold',
    size: 90,
    cell: (info) => {
      const r = info.row.original;
      return flagCell(r.rcvHold, r.rcvHoldsOpen);
    },
  },
  { accessorKey: 'rcvStorageLfdDate', header: 'Storage LFD', size: 90 },
  { accessorKey: 'customerName', header: 'Customer', size: 160 },
  { accessorKey: 'billToName', header: 'Bill To', size: 160 },
  { accessorKey: 'overageShortage', header: 'O/S', size: 80 },
  {
    accessorKey: 'damages',
    header: 'Damages',
    size: 90,
    cell: (info) => {
      const r = info.row.original;
      return flagCell(r.damages, r.damagesOpen);
    },
  },
  { accessorKey: 'rcvShipDate', header: 'Ship Date', size: 90 },
];

const SHIPPING_COLUMNS: ColumnDef<WhseShippingRow>[] = [
  { accessorKey: 'blNum', header: 'BL #', size: 60 },
  { accessorKey: 'blWhse', header: 'Whse', size: 80 },
  { accessorKey: 'productCode', header: 'Product Code', size: 200 },
  { accessorKey: 'pickType', header: 'Added By', size: 100 },
  { accessorKey: 'pickValue', header: 'Added From', size: 100 },
  { accessorKey: 'pickPolicy', header: 'Pick Policy', size: 90 },
  { accessorKey: 'callOffNum', header: 'Call Off #', size: 90 },
  { accessorKey: 'onhandMasterQty', header: 'Avail Qty', size: 80 },
  { accessorKey: 'onhandMasterUom', header: 'Avail UoM', size: 80 },
  { accessorKey: 'onhandSecondaryQty', header: '2nd Avail Qty', size: 95 },
  { accessorKey: 'onhandSecondaryUom', header: '2nd Avail UoM', size: 95 },
  { accessorKey: 'blQty', header: 'Qty', size: 70 },
  { accessorKey: 'blUom', header: 'UoM', size: 70 },
  { accessorKey: 'blLbs', header: 'Lbs', size: 80 },
  { accessorKey: 'blKgs', header: 'Kgs', size: 80 },
  { accessorKey: 'blShipDate', header: 'Ship Date', size: 90 },
  { accessorKey: 'rcvPronumber', header: 'MR', size: 90 },
  { accessorKey: 'checkInDatetime', header: 'Check In', size: 120 },
  { accessorKey: 'customerName', header: 'Customer', size: 160 },
];

const receivingFields = (s: WhseReceivingSummary) => [
  { label: 'Seal No.', value: s.sealNumber },
  { label: 'Cargo Lbs', value: s.cargoLbs },
  { label: 'Cargo Kgs', value: s.cargoKgs },
  { label: 'Door', value: s.whseDoor },
  { label: 'MR CCC Terminal', value: s.terminal },
  { label: 'CCC', value: s.cccStandard },
  { label: 'Account Type', value: s.accountType },
  { label: 'Receiving Type', value: s.receivingType },
  { label: 'Outbound Mode', value: s.outboundMode },
  { label: '3rd Party System', value: s.thirdPartySystem },
  { label: 'Label Type', value: s.labelType },
  { label: 'Show MR Qty', value: s.showMrQty },
  { label: 'Show MR Details', value: s.showMrDetails },
  { label: 'MR Hold', value: s.mrHold },
];

const shippingFields = (s: WhseShippingSummary) => [
  { label: 'Seal No.', value: s.sealNumber },
  { label: 'Cargo Lbs', value: s.cargoLbs },
  { label: 'Cargo Kgs', value: s.cargoKgs },
  { label: 'Door', value: s.whseDoor },
  { label: 'BL CCC Terminal', value: s.blTerminal },
  { label: 'CCC', value: s.blCccStandard },
  { label: 'BL Comments', value: s.blComments },
  { label: 'Whse Transfer', value: s.whseTransfer },
  { label: 'Pre Load', value: s.blPreload },
  { label: 'Load Date/Time', value: s.blLoadDatetime },
  { label: 'BL Hold', value: s.blHold },
  { label: '3rd Party System', value: s.thirdPartySystem },
];

const WhseView: React.FC<Props> = ({ pronumber }) => {
  const query = useQuery({
    queryKey: ['whse', pronumber],
    queryFn: () => whseService.getWhse(pronumber),
    staleTime: 30 * 1000,
  });

  const receivingColumns = useMemo(() => RECEIVING_COLUMNS, []);
  const shippingColumns = useMemo(() => SHIPPING_COLUMNS, []);

  if (query.isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div
          className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"
          role="status"
          aria-label="Loading warehouse data"
        />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="px-3 pt-3">
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
          <p className="text-sm text-red-700 dark:text-red-400">
            Failed to load warehouse data.
          </p>
        </div>
      </div>
    );
  }

  const data = query.data;

  if (data.flavor === 'none') {
    return (
      <div className="px-3 pt-3 pb-6">
        <div className="rounded-lg border border-slate-200 dark:border-gray-700 bg-panel p-10 flex items-center justify-center min-h-[200px]">
          <div className="text-center">
            <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
              Whse
            </div>
            <div className="text-sm text-slate-500 dark:text-gray-400">
              No warehouse data for this move type
              {data.movetype ? ` (${data.movetype})` : ''}.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col px-3 pt-3 pb-3 gap-3 min-h-0">
      {data.flavor === 'receiving' ? (
        <>
          <SummaryHeader
            title="Receiving (MR)"
            totalQty={data.totalQty}
            whseInstructionsCount={data.whseInstructionsCount}
            fields={receivingFields(data.summary)}
          />
          <div className="flex-1 min-h-[240px]">
            <TanTable<WhseReceivingRow>
              // Distinct key per flavor: the Receiving (MR) and Shipping (BL) grids share
              // this ternary slot with different settingsKeys. data.flavor is constant for
              // a given pro, but if this Whse tab stays mounted across a navigate from an
              // MR pro to a BL pro, without a key React reuses ONE TanTable and its
              // autosave cross-writes the other flavor's saved layout. Key = fresh mount.
              key="pro-whse-receiving"
              data={data.rows}
              columns={receivingColumns}
              cardTitle="MR Line Items"
              settingsKey="pro-whse-receiving"
              seedLayoutFromCache
              fillContainer
              enableSearch
              enableReset
              enableShowHideCols
              enableColumnFilters
              enableZebraStriping={false}
            />
          </div>
        </>
      ) : (
        <>
          <SummaryHeader
            title="Shipping (BL)"
            totalQty={data.totalQty}
            whseInstructionsCount={data.whseInstructionsCount}
            fields={shippingFields(data.summary)}
          />
          <div className="flex-1 min-h-[240px]">
            <TanTable<WhseShippingRow>
              // See the Receiving grid above — distinct key keeps this Shipping grid a
              // separate instance so the two flavors never cross-write each other's layout.
              key="pro-whse-shipping"
              data={data.rows}
              columns={shippingColumns}
              cardTitle="BL Line Items"
              settingsKey="pro-whse-shipping"
              seedLayoutFromCache
              fillContainer
              enableSearch
              enableReset
              enableShowHideCols
              enableColumnFilters
              enableZebraStriping={false}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default WhseView;
